import { Injectable } from '@angular/core';
import { ConfigBaseFilter, ConfigService, ListLayout, ListSortOption } from 'gung-list';
import { BehaviorSubject, Observable, Subject, catchError, filter, first, forkJoin, map, of, switchMap } from 'rxjs';
import { GridViewProductCard } from '../components/product-grid-view/product-grid-view.component';
import { Product } from '../models';
import { AuthService } from './auth/auth.service';
import { BaseViewConfigService, ProductViewType } from './base-view-config/base-view-config.service';
import { ProductAssortmentParentService } from './product-assortment-parent/product-assortment-parent.service';
import { ProductListConfigService } from './product-list-config/product-list-config.service';
import { ProductListV2ConfigService } from './product-list-v2-config/product-list-v2-config.service';
import { ProductSortService } from './product-sort/product-sort.service';

@Injectable({
  providedIn: 'root'
})
export class NavbarSearchViewMoreListConfigService implements ConfigService<Product> {
  searchDisabled = true;
  productsSubject: Subject<Product[]> = new BehaviorSubject(null);
  disableViewMoreSearch: boolean = false;
  assortmentSubject: Subject<string> = new BehaviorSubject(null);

  constructor(
    protected baseViewConfigService: BaseViewConfigService,
    protected productListConfigService: ProductListConfigService,
    protected productListV2ConfigService: ProductListV2ConfigService,
    protected productSortService: ProductSortService,
    protected authService: AuthService,
    protected productAssortmentParentService: ProductAssortmentParentService
  ) { }

  getItems(): Observable<Product[]> {
    return this.assortmentSubject.pipe(
      switchMap((assortmentId: string | null) => {
        const assortment$ = assortmentId != null
          ? of(assortmentId)
          : this.authService.getCurrentUser().pipe(
            filter(user => !!user),
            map(user => user.assortment)
          );

        return assortment$.pipe(
          switchMap(assortmentId =>
            this.productsSubject.asObservable().pipe(
              map(products => this.productSortService.sortProducts(products)),
              switchMap(sortedProducts => {
                const ids = sortedProducts.map(p => p.id);
                const searchCategories = sortedProducts[0]?.['productType'] === "concept" || sortedProducts[0]?.['assortment'];
                return this.getPathInfo(ids, assortmentId, searchCategories).pipe(
                  map(paths => {
                    return sortedProducts.map(product => {
                      product.extra = product.extra || {};
                      product.extra._routerUrl = paths.find(p => p.id === product.id)?.path || null;
                      return product;
                    });
                  })
                );
              })
            )
          )
        );
      })
    );
  }

  getFilters(): ConfigBaseFilter<Product>[] {
    return [];
  }

  getSortOptions(): ListSortOption<Product>[] {
    return [];
  }

  getBatchSizes(): number[] {
    return [Number.MAX_SAFE_INTEGER];
  }

  getLayouts(): ListLayout<Product>[] {
    if (this.baseViewConfigService.productViewType === ProductViewType.productV2) {
      return this.productListV2ConfigService.getLayouts();
    } else {
      return this.productListConfigService.getLayouts();
    }
  }

  getProductGridCardComponent(item: GridViewProductCard): any {
    return this.productListV2ConfigService.getProductGridCardComponent(item);
  }

  getItemId(item: Product) {
    return item.id;
  }

  getSearchTerms(item: Product) {
    return [item.id, item.name];
  }

  protected getPathInfo(ids: string[], userAssortment: string, searchCategories: boolean): Observable<{ id: string, path: string }[]> {
    if (ids.length === 0) {
      return of([]);
    }

    if (searchCategories) {
      return forkJoin(
        ids.map(id => this.productAssortmentParentService.getSearchAssortments(id).pipe(first(), catchError(() => of({ assortments: [], concepts: [] }))))
      ).pipe(
        map(results => {
          return results.map(res => {
            return res.concepts.map(a => {
              const _path = a.path.map(p => p.id);
              _path.splice(0, 1, '/articles');
              return { id: a.concept.id, path: _path.join('/') };
            });
          }).flat();
        })
      );
    } else {
      return this.productAssortmentParentService.getProductAssortmentParentByProductIds(ids, userAssortment).pipe(
        first(),
        catchError(() => of([])),
        map(results => {
          const result: { id: string, path: string }[] = [];

          for (const key in results) {
            if (results.hasOwnProperty(key)) {
              const element: { id: string }[] = structuredClone(results[key]);
              element.shift();
              result.push({
                id: key,
                path: ['/articles', ...element.map(e => e.id)].join('/')
              });
            }
          }

          return result;
        })
      );
    }
  }
}
